import React, { useState } from 'react';
import {
  Collapse,
  Fab,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  TableContainer,
} from '@material-ui/core';
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Map as MapIcon,
} from '@material-ui/icons';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import PF2MDatePickerOutlined from '~/components/PF2MDatePickerOutlined';
import PF2MSearchSelectOutlined from '~/components/PF2MSearchSelectOutlined';
import {
  parseDate,
  formatDateToCurrentLocale,
} from '~/utils/moment';
import PF2MOutlinedInput from '~/components/PF2MOutlinedInput';
import classNames from 'classnames';


const renderItems = ({
  items,
  itemKey = 'id',
  itemText = 'name',
  filterKey = null,
  filterValue = null,
  defaultLabel = null,
  filterFunction = null,
  data = null,
  defaultValue = 0,
  translate,
}) => {
  let filteredItems = [];
  if (filterFunction) {
    filteredItems = [...items.filter(item => filterFunction(item, data))];
  } else {
    filteredItems = [...items.filter(i => filterKey === null || i[filterKey] === filterValue)];
  }
  return [
    { value: defaultValue, label: (defaultLabel || translate('common:Select')) },
    ...filteredItems.map(e => (
      { value: e[itemKey], label: e[itemText] }
    )),
  ];
};

const CollapsibleRow = ({
  classes,
  columns,
  data,
  collapsibleData,
  collapsibleColumns,
  updateItem,
  customItem,
}) => {
  const { t: translate } = useTranslation();
  const [open, setOpen] = useState(false);

  const renderField = (r, column) => {
    if (column.renderCustomComponent) {
      return column.renderCustomComponent(r);
    }

    if (column.datePicker) {
      return (
        <PF2MDatePickerOutlined
          onChange={e => updateItem(r, column.field, e)}
          value={parseDate(formatDateToCurrentLocale(r[column.field]))}
          disabled={!column.editable || r.blocked}
          disableFuture={column.disableFuture !== undefined ? column.disableFuture : true}
        />
      );
    }

    if (column.selectItems) {
      return (
        <PF2MSearchSelectOutlined
          fullWidth={false}
          className={classes.select}
          onChange={e => updateItem && updateItem(data, column.field, e.target.value)}
          value={(r[column.field] || 0)}
          disabled={!column.editable}
          style={{ width: column.width || '' }}
        >
          {renderItems({
            items: column.selectItems,
            itemKey: column.itemKey,
            itemText: column.itemText,
            filterKey: column.filterKey,
            filterValue: r[column.filterValue],
            defaultLabel: column.defaultLabel,
            filterFunction: column.filterFunction || null,
            row: data,
            defaultValue: column.defaultValue || 0,
            translate,
          })}
        </PF2MSearchSelectOutlined>
      );
    }

    const newProps = {};

    if (column.updateOnChange) {
      newProps.onChange = e => updateItem(r, column.field, e.target.value);
    } else {
      newProps.onBlur = e => updateItem(r, column.field, e.target.value);
    }

    const value = (
      column.translateField
        ? translate(`common:${data[column.field]}`)
        : data[column.field]
          || column.defaultValue
          || (column.type === 'number' ? 0 : '')
    );

    if (!column.editable) {
      return column.truncate
        ? `${value.toString().substring(1, 50)}...`
        : value;
    }

    return (
      <PF2MOutlinedInput
        value={value.toString()}
        {...newProps}
        className={classNames(classes.textField)}
        style={{ minWidth: column.width || 200, maxHeight: 42 }}
        type={column.type === 'number' ? 'number' : 'text'}
        inputProps={{ min: 0 }}
      />
    );
  };

  return (
    <>
      <TableRow>
        <TableCell align="left" width={columns[0].width}>
          <IconButton
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        {columns.map(col => (
          <TableCell align="left" width={col.width}>
            {renderField(data, col)}
          </TableCell>
        ))}
        {customItem && (
          <TableCell width="50px">
            <Fab
              size="small"
              color="primary"
              aria-label="Custom"
              onClick={() => customItem && customItem(data)}
            >
              <MapIcon />
            </Fab>
          </TableCell>
        )}
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
          <Collapse in={open} timeout="auto">
            <Table size="small">
              <TableHead>
                <TableRow>
                  {collapsibleColumns.map(col => (
                    <TableCell align="left" width={col.width}>{col.title}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {collapsibleData
                  ?.filter(d => d.uid === data.uid)
                  .map(row => (
                    <TableRow key={row.id}>
                      {collapsibleColumns.map(col => (
                        <TableCell align="left" width={col.width}>{renderField(row, col)}</TableCell>
                      ))}
                    </TableRow>
                  ))
                }
              </TableBody>
            </Table>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

CollapsibleRow.propTypes = {
  classes: PropTypes.object.isRequired,
  data: PropTypes.object.isRequired,
  columns: PropTypes.array.isRequired,
  collapsibleData: PropTypes.array.isRequired,
  collapsibleColumns: PropTypes.array.isRequired,
  updateItem: PropTypes.func,
  customItem: PropTypes.func,
};

CollapsibleRow.defaultProps = {
  updateItem: null,
  customItem: null,
};

const CollapsibleTable = ({
  classes,
  dataReport,
  columnsReport,
  collapsibleData,
  collapsibleColumns,
  updateItem,
  customItem,
}) => (
  <TableContainer component={Paper} style={{ maxHeight: '350px', overflowY: 'scroll' }}>
    <Table>
      <TableHead>
        <TableRow>
          <TableCell />
          {columnsReport.map(col => (
            <TableCell align="left">{col.title}</TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {dataReport.map(data => (
          <CollapsibleRow
            classes={classes}
            key={data.name}
            data={data}
            columns={columnsReport}
            collapsibleData={collapsibleData}
            collapsibleColumns={collapsibleColumns}
            updateItem={updateItem}
            customItem={customItem}
          />
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

CollapsibleTable.propTypes = {
  classes: PropTypes.object.isRequired,
  columnsReport: PropTypes.object.isRequired,
  dataReport: PropTypes.array.isRequired,
  collapsibleData: PropTypes.array.isRequired,
  collapsibleColumns: PropTypes.array.isRequired,
  updateItem: PropTypes.func,
  customItem: PropTypes.func,
};

CollapsibleTable.defaultProps = {
  updateItem: null,
  customItem: null,
};


export default CollapsibleTable;
